import { useContext, useEffect, useState } from 'react';

import SaveArtworkIcon from '@/assets/all-icons/save-artwork-icon';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';

import OptionItem from './ArtworkViewOptions';

function resolveSaveStatus(cacheHandler, artworkData, socialsData) {
  if (!artworkData?._id) {
    return false;
  }

  const cachedAction = cacheHandler.getCacheValue(`user_actions.saved_artworks.${artworkData._id}`);
  if (cachedAction) {
    return Boolean(cachedAction.data);
  }

  if (socialsData) {
    return Boolean(socialsData.save_artwork);
  }

  return false;
}

export default function SaveArtwork({
  artworkData: artworkDataProp,
  socialsData: socialsDataProp,
  setSocialsData: setSocialsDataProp,
  onCompleted,
}) {
  const t = useTranslations('common.shared.artwork_view.options');
  const context = useContext(ArtworkDataContext);
  const artworkData = artworkDataProp ?? context?.artworkData ?? null;
  const socialsData = socialsDataProp ?? context?.socialsData ?? null;
  const setSocialsData = setSocialsDataProp ?? context?.setSocialsData ?? null;
  const { stateHandler, cacheHandler } = useStateHandler();
  const [saveStatus, setSaveStatus] = useState(() => resolveSaveStatus(cacheHandler, artworkData, socialsData));
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });

  useEffect(() => {
    setSaveStatus(resolveSaveStatus(cacheHandler, artworkData, socialsData));
  }, [cacheHandler, artworkData, socialsData]);

  async function saveArtwork() {
    if (!artworkData?._id) {
      return;
    }

    const { response } = await UserService.saveItem("artwork", artworkData._id);
    if (!response?.ok) {
      if (window?.SuarteDebug?.artworkSave) {
        // Debug flag is opt-in to avoid noisy production logs.
        console.warn("[SaveArtwork] save request failed", {
          artworkId: artworkData._id,
          status: response?.status ?? null,
        });
      }
      return;
    }
    setSaveStatus(true);

    if(socialsData && typeof setSocialsData === "function") {
      setSocialsData({
        ...socialsData,
        save_artwork: true
      });
    }

    stateHandler.set("temporalPopup", { text: `${artworkData.artwork_about.artwork_title} ${t('save.saved_successfully')}`, type: "no-navigation" });
    cacheHandler.storeInCache(`user_actions.saved_artworks.${artworkData._id}`, true, { type: "@cache/no-expiration" });
    cacheHandler.triggerAction("SAVE_ARTWORK");
    if (typeof onCompleted === "function") {
      onCompleted();
    }
  }

  async function unSaveArtwork() {
    if (!artworkData?._id) {
      return;
    }

    const { response } = await UserService.unsaveItem("artwork", artworkData._id);
    if (!response?.ok) {
      if (window?.SuarteDebug?.artworkSave) {
        console.warn("[SaveArtwork] unsave request failed", {
          artworkId: artworkData._id,
          status: response?.status ?? null,
        });
      }
      return;
    }
    setSaveStatus(false);

    if(socialsData && typeof setSocialsData === "function") {
      setSocialsData({
        ...socialsData,
        save_artwork: false
      });
    }
        
    stateHandler.set("temporalPopup", { text: `${artworkData.artwork_about.artwork_title} ${t('save.removed_from_saved_artworks')}`, type: "no-navigation" });
    cacheHandler.storeInCache(`user_actions.saved_artworks.${artworkData._id}`, false, { type: "@cache/no-expiration" });
    cacheHandler.triggerAction("SAVE_ARTWORK");
    if (typeof onCompleted === "function") {
      onCompleted();
    }
  }

  if(saveStatus) {
    return (
      <OptionItem 
        text={t('menu.saved')}
        icon={<SaveArtworkIcon className="save-artwork-icon fill"/>} 
        onClick={()=>privateActionHandler(unSaveArtwork)}
      />
    );
  } else {
    return (
      <OptionItem 
        text={t('menu.save_artwork')}
        icon={<SaveArtworkIcon className="save-artwork-icon"/>} 
        onClick={()=>privateActionHandler(saveArtwork)}
      />
    );
  }
}
